import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useAuthStore } from "../../stores/auth.store";
import { ConversationList } from "../../components/messages/ConversationList";
import { Skeleton, SkeletonText, SkeletonCircle } from "../../components/ui/Skeleton";
import { usePolling } from "../../hooks/usePolling";
import * as messageApi from "../../api/message.api";
import type { ConversationPreview } from "../../api/message.api";

export function InboxPage() {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const { conversations: convos } = await messageApi.getConversations();
      setConversations(convos);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login");
      return;
    }
    if (user) {
      fetchConversations();
    }
  }, [user, isLoading, navigate, fetchConversations]);

  // Poll for updates every 10s (active) / 30s (background)
  usePolling(fetchConversations, {
    activeInterval: 10_000,
    backgroundInterval: 30_000,
    enabled: !!user && !loading,
  });

  if (isLoading || loading) {
    return (
      <div className="pb-20">
        <div className="border-b border-gray-200 px-4 py-3">
          <SkeletonText className="h-5 w-24" />
        </div>
        <div className="divide-y divide-gray-100">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <SkeletonCircle className="h-12 w-12" />
              <div className="flex-1 space-y-2">
                <SkeletonText className="h-4 w-32" />
                <SkeletonText className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pb-20">
        <div className="border-b border-gray-200 px-4 py-3">
          <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
        </div>
        <div className="py-12 text-center">
          <p className="mb-3 text-sm text-gray-500">Failed to load conversations.</p>
          <button
            onClick={() => { setLoading(true); setError(false); fetchConversations(); }}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="border-b border-gray-200 px-4 py-3">
        <h1 className="text-lg font-semibold text-gray-900">Messages</h1>
      </div>
      <ConversationList conversations={conversations} />
    </div>
  );
}
